import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BaseFilterDefinition,
    DashboardDefinitionDTO,
    DashboardFilterRequestDTO,
    DashboardFilterTypeInd,
    DashboardsServiceV2,
    FilterChangeEvent,
    organisationDataSetChange,
    WidgetContainerDTO,
    WidgetContainerRowDTO,
    WidgetDataDTO,
    WidgetDataSetRequestDTO,
    WidgetDefinitionDTO,
    WidgetRowDto,
} from '../../../core/services/dashboardsv2.service';
import { HelpModalComponent } from 'src/app/shared/components/modals/help-modal/help-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, filter, pairwise, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MapDataDto } from 'src/app/shared/models/maps/map-data-dto.model';
import { MapModalComponent } from './modals/map-modal/map-modal.component';
import BaseComponent from 'src/app/core/base/base-component.component';
import { MessageService } from 'src/app/core/services/message.service';
import html2canvas from 'html2canvas';
import { DashboardViewModalComponent } from './modals/dashboard-view-modal/dashboard-view-modal.component';
import { DashboardViewDto } from 'src/app/shared/models/dashboards/dashboard-view-dto.model';
import { SharingLevel } from 'src/app/shared/models/movement';
import { DashboardViewFilterDto } from 'src/app/shared/models/dashboards/dashboard-view-filter-dto.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { DashboardViewWidgetContainerDto } from 'src/app/shared/models/dashboards/dashboard-view-widget-container-dto.model';
import { DashboardViewWidgetDto } from 'src/app/shared/models/dashboards/dashboard-view-widget-dto.model';
import {
    DashboardExportPageSize,
    DashboardExportPdfBuilder,
    IDashboardExportFilter,
    IDashboardExportImage,
} from './dashboard-export-pdf-builder';
import { environment } from '../../../../environments/environment';
import { DashboardWidgetLoadingService } from '../dashboard-widget-loading-service';
import {
    ConfirmationComponent,
    ConfirmDialogModel,
} from '../../../shared/components/confirmation/confirmation.component';

@Component({
    selector: 'app-dashboardv2',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    providers: [DashboardsServiceV2],
})
export class DashboardComponent extends BaseComponent implements OnInit, OnDestroy {
    loadingIndicator: boolean = true;
    dashboardMode: string = 'nodata';
    dashboardTypeInd: number;
    customDashboardId: number;
    locationService: Location;
    vimeoVideoUrl: string = null;
    dashboardService: DashboardsServiceV2;
    dashboardConfig: DashboardDefinitionDTO;
    widgetDataSetRequestDTO: WidgetDataSetRequestDTO = {
        dataSetId: 0,
        organisationId: 0,
        filters: [],
    };

    isProDashboard: boolean = false;

    currentDataSetRequest: organisationDataSetChange;

    isExportingPdf: boolean = false;
    public totalExportSteps: number = 0;
    public exportStepsCompleted: number = 0;
    public widgetsLoading: boolean = false;
    public widgetLoadingTimeoutTimer: ReturnType<typeof setInterval> | null = null;
    public widgetLoadingHasTimedOut: boolean = false;

    private latestOpenedContainer: WidgetContainerDTO;

    private readonly _subscriptions: Subscription = new Subscription();
    private readonly _v3RefreshSubject = new Subject<void>();
    private readonly _widgetLoadingTimeoutMs: number = 30 * 1000;

    @ViewChild('printDiv') printDiv: ElementRef<HTMLElement>;
    @ViewChild('exportHeader', { read: ElementRef }) exportHeader: ElementRef<HTMLElement>;
    @ViewChild('exportWidgets') exportWidgets: ElementRef<HTMLElement>;

    get isUserAdmin(): boolean {
        return this.authService.isUserSportsEyeAdmin() || this.authService.isUserOrganisationAdmin();
    }

    constructor(
        activatedRoute: ActivatedRoute,
        dashboardService: DashboardsServiceV2,
        location: Location,
        private authService: AuthService,
        private dialog: MatDialog,
        private router: Router,
        private messageService: MessageService,
        private readonly _dashboardWidgetLoadingService: DashboardWidgetLoadingService
    ) {
        super();
        this.dashboardTypeInd = parseInt(activatedRoute.snapshot.paramMap.get('typeInd'));
        this.customDashboardId = parseInt(activatedRoute.snapshot.paramMap.get('customDashboardId'));
        this.dashboardService = dashboardService;
        this.locationService = location;

        this.isProDashboard = this.dashboardTypeInd == 50;
    }

    /** Load Dashboard Configuration from server */
    ngOnInit(): void {
        this._subscriptions.add(
            this._dashboardWidgetLoadingService.widgetsLoading$
                .pipe(
                    pairwise(),
                    tap(([prevIds, newIds]) => {
                        this.widgetsLoading = newIds.length !== 0;

                        // All widgets are loaded, clear the timer and mark the UI as loaded
                        if (newIds.length === 0) {
                            this.widgetLoadingHasTimedOut = false;

                            clearTimeout(this.widgetLoadingTimeoutTimer);
                            this.widgetLoadingTimeoutTimer = null;

                            return;
                        }

                        // More widgets have started loading, reset the timer
                        if (newIds.length > prevIds.length) {
                            clearTimeout(this.widgetLoadingTimeoutTimer);
                            this.widgetLoadingTimeoutTimer = null;
                            this.widgetLoadingHasTimedOut = false;
                        }

                        // If we get to here, some widgets are loading, set a timer for timeout (if not already set)
                        if (this.widgetLoadingTimeoutTimer === null) {
                            this.widgetLoadingTimeoutTimer = setTimeout(() => {
                                this.widgetsLoading = false;
                                this.widgetLoadingHasTimedOut = true;
                            }, this._widgetLoadingTimeoutMs);
                        }
                    })
                )
                .subscribe()
        );

        // V3 dashboard refresh — switchMap ensures concurrent filter-change calls cancel
        // any in-flight request, so only the latest filter state reaches the backend.
        this._subscriptions.add(
            this._v3RefreshSubject.pipe(
                switchMap(() =>
                    this.dashboardService
                        .triggerFilterChangeEvent(this.widgetDataSetRequestDTO, this.dashboardConfig)
                        .pipe(
                            catchError((error) => {
                                this.handleError(error);
                                this.loadingIndicator = false;
                                return of([] as WidgetDataDTO[]);
                            })
                        )
                ),
                takeUntil(this.subscriptionComplete)
            ).subscribe((updatedWidgetsData: WidgetDataDTO[]) => {
                let flatIndex = 0;
                this.dashboardConfig.rows.forEach((row) => {
                    row.containers.forEach((container) => {
                        container.widgetRows.forEach((widgetRow) => {
                            widgetRow.widgets.forEach((widget, widgetIndex) => {
                                if (updatedWidgetsData && updatedWidgetsData.length > flatIndex) {
                                    const dataForWidget = updatedWidgetsData[flatIndex];
                                    widgetRow.widgets[widgetIndex] = {
                                        ...widget,
                                        data: dataForWidget,
                                        loading: false,
                                    } as WidgetDefinitionDTO;
                                } else {
                                    widgetRow.widgets[widgetIndex] = {
                                        ...widget,
                                        loading: false,
                                    } as WidgetDefinitionDTO;
                                }
                                flatIndex++;
                            });
                        });
                    });
                });
                this.loadingIndicator = false;
            })
        );

        this.loadingIndicator = true;
        this.dashboardService
            .getDashboardConfigurationForUser(this.dashboardTypeInd, this.customDashboardId)
            .subscribe((data: DashboardDefinitionDTO) => {
                if (data.organisationDataSets.length > 0) {
                    if (data.organisationDataSets[0].dataSets.length > 0) {
                        this.currentDataSetRequest = {
                            organisationId: data.organisationDataSets[0].id,
                            dataSetId: data.organisationDataSets[0].dataSets[0].id,
                        };

                        this.dashboardMode = 'data';
                        this.widgetDataSetRequestDTO = {
                            organisationId: this.currentDataSetRequest.organisationId,
                            dataSetId: this.currentDataSetRequest.dataSetId,
                            filters: data.filters.map(
                                (x) =>
                                    <DashboardFilterRequestDTO>{
                                        filterId: x.filterId,
                                        filterTypeInd: x.filterTypeInd,
                                        filterValues: x.selection,
                                    }
                            ),
                        };
                    } else {
                        this.dashboardMode = 'nodata';
                    }
                }

                this.dashboardConfig = data;
                this.loadingIndicator = false;

                if (data.version === 3 && this.dashboardMode === 'data') {
                    this.updateWidgetDataSetRequest();
                }
            });
        this.subscribeToMapDialogButtonClick();
    }

    public ngOnDestroy(): void {
        this._dashboardWidgetLoadingService.removeAll();

        this._subscriptions.unsubscribe();
    }

    public getPrintButtonTooltip(): string {
        if (this.isExportingPdf) {
            return 'The PDF is currently being printed. Please wait.';
        }

        if (this.widgetsLoading) {
            return 'Printing will be available after all charts have loaded. Please wait.';
        }

        return 'Print the dashboard to a PDF.';
    }

    back() {
        this.locationService.back();
    }

    public async tryExportPdf(): Promise<void> {
        if (!this.widgetLoadingHasTimedOut) {
            return await this._exportPDF();
        }

        const message =
            'Not all charts have loaded yet. You can still print the PDF, but some charts may not display correctly. Do you want to continue?';
        const dialogData = new ConfirmDialogModel('Confirm Printing', message, 'PRINT');
        const dialogRef = this.dialog.open(ConfirmationComponent, {
            maxWidth: '400px',
            data: dialogData,
            panelClass: 'seDialog',
        });

        this._subscriptions.add(
            dialogRef
                .afterClosed()
                .pipe(
                    tap(async (confirmed) => {
                        if (confirmed) {
                            await this._exportPDF();
                        }
                    })
                )
                .subscribe()
        );
    }

    // Export dashboard to PDF with optimized settings for smaller file size
    private async _exportPDF(): Promise<void> {
        this.isExportingPdf = true;
        this.totalExportSteps = 0;
        this.exportStepsCompleted = 0;

        // Allow Angular change detection to update UI before starting heavy processing
        await new Promise((resolve) => setTimeout(resolve, 10));

        try {
            const widgetRowElements = this.exportWidgets.nativeElement.querySelectorAll('.container-row');

            // Header plus a synchronous and asynchronous html2canvas step for each widget row
            this.totalExportSteps = 1 + 2 * widgetRowElements.length;

            const headerCanvasRenderer = this._renderElementAndIncreaseStep(this.exportHeader.nativeElement);
            const renderers: Promise<HTMLCanvasElement>[] = [];

            for (let i = 0; i < widgetRowElements.length; i++) {
                const element = widgetRowElements[i] as HTMLElement;

                // Allow Angular change detection to run to update UI progress bar. This synchronous step is quite heavy and
                // locks up the UI otherwise. This isn't perfect, but for long exports we need to give the user some progress updates.
                await new Promise((resolve) => setTimeout(resolve, 1));

                renderers.push(this._renderElementAndIncreaseStep(element));

                this.exportStepsCompleted += 1;
            }

            const headerCanvas = await headerCanvasRenderer;
            const results = await Promise.all(renderers);

            const headerImage: IDashboardExportImage = {
                dataUrl: headerCanvas.toDataURL('image/jpeg', 1),
                width: headerCanvas.width,
                height: headerCanvas.height,
            };
            const images = results.map((canvas): IDashboardExportImage => {
                return {
                    dataUrl: canvas.toDataURL('image/jpeg', 1),
                    width: canvas.width,
                    height: canvas.height,
                };
            });

            const geographicLevelsFilter: BaseFilterDefinition | undefined = this.dashboardConfig.filters.find(
                (f) => f.filterTypeInd === DashboardFilterTypeInd.GeographicLevelSelection
            );

            const pdfBuilder = new DashboardExportPdfBuilder(
                this._getLocalPaperSize(),
                headerImage,
                images,
                this._mapFiltersForExport(),
                geographicLevelsFilter,
                this._getSelectedDataSetNameForExport()
            );
            const pdf = await pdfBuilder.getPdf();
            const fileName = `${this.dashboardConfig?.title || 'Dashboard'}.pdf`;

            pdf.save(fileName);
        } catch (error) {
            // Error occurred during PDF export
            this.messageService.showError('Failed to export PDF. Please try again.');

            console.error(error);
        } finally {
            this.isExportingPdf = false;
        }
    }

    /**
     * Returns the name of the currently selected organisation data set, if possible, but only if
     * there is more than one to choose from, returning 'undefined' otherwise, as required for
     * the dashboard export.
     * @private
     */
    private _getSelectedDataSetNameForExport(): string | undefined {
        if (this.currentDataSetRequest !== undefined && this.dashboardConfig?.organisationDataSets?.length > 0) {
            const organisationDataSets = this.dashboardConfig.organisationDataSets.find(
                (d) => d.id === this.currentDataSetRequest.organisationId
            );

            if (organisationDataSets.dataSets.length > 1) {
                return (
                    organisationDataSets.dataSets.find((s) => s.id === this.currentDataSetRequest.dataSetId)?.name ??
                    undefined
                );
            }
        }

        return undefined;
    }

    private _renderElementAndIncreaseStep(el: HTMLElement): Promise<HTMLCanvasElement> {
        return new Promise(async (resolve) => {
            const result = await html2canvas(el as HTMLElement, {
                scale: 1,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                onclone: async (doc) => {
                    // Performance improvement: replace canvases with images of the same contents
                    doc.querySelectorAll('canvas').forEach((canvas) => {
                        try {
                            const img = doc.createElement('img');

                            img.src = canvas.toDataURL('image/png');
                            img.style.width = `${canvas.clientWidth}px`;
                            img.style.height = `${canvas.clientHeight}px`;
                            canvas.replaceWith(img);
                        } catch (_) {}
                    });

                    // A little hack, but we need to wait for the page to settle after adding the images and
                    // rendering the canvases, so that the charts can load fully ready for rendering
                    await new Promise((res) => setTimeout(res, 100));
                },
            });

            this.exportStepsCompleted += 1;

            resolve(result);
        });
    }

    private _getLocalPaperSize(): DashboardExportPageSize {
        const countryCode = environment.instanceCountryISO3166 ?? 'AUS';

        switch (countryCode.toUpperCase()) {
            case 'USA':
            case 'CAN':
                return 'letter';
            default:
                return 'a4';
        }
    }

    private _mapFiltersForExport(): IDashboardExportFilter[] {
        return this.dashboardConfig.filters.map((filter): IDashboardExportFilter => {
            return {
                title: filter.label,
                values: this._mapFilterValuesText(filter),
                filterTypeId: filter.filterTypeInd,
                options: filter.options,
                selection: filter.selection,
            };
        });
    }

    private _mapFilterValuesText(filter: BaseFilterDefinition): string[] {
        switch (filter.filterTypeInd) {
            case DashboardFilterTypeInd.GenericRange:
                return [this._mapSelectedFilterRange(filter)];
            case DashboardFilterTypeInd.GeographicLevelSelection:
                return this._mapGeographicLevelFilterSelection(filter);
            default:
                return this._mapSelectedFilterOptionNames(filter);
        }
    }

    private _mapSelectedFilterOptionNames(filter: BaseFilterDefinition): string[] {
        if (filter.options === undefined) {
            return [(filter.selection[0] ?? 'Unknown').toString()];
        }

        return filter.selection.map((selectedValue) => {
            const option = filter.options.find((opt) => opt.id === selectedValue);

            return option ? option.name : 'Unknown';
        });
    }

    private _mapSelectedFilterRange(filter: BaseFilterDefinition): string {
        if (filter.selection.length < 2) {
            return 'Unknown';
        }

        const rangeFrom = filter.selection[0];
        const rangeTo = filter.selection[1];

        return `${rangeFrom} - ${rangeTo}`;
    }

    private _mapGeographicLevelFilterSelection(filter: BaseFilterDefinition): string[] {
        const locations = filter.options.map((option) => option.locations).flat();

        return filter.selection.map((selectedId) => {
            const selectedLocation = locations.find((loc) => loc.id === selectedId);

            return selectedLocation.name ?? 'Unknown';
        });
    }

    /** Update the current widget data set request */
    updateWidgetDataSetRequest() {
        // collapse collapseOnUpdate containers
        this.dashboardConfig.rows.forEach((row) =>
            row.containers.forEach((container) => {
                //keep the latest opened container open so it reloads automatically
                if (container.collapseOnUpdate && container != this.latestOpenedContainer) {
                    container.isCollapsed = true;
                }
            })
        );
        this.widgetDataSetRequestDTO = {
            organisationId: this.currentDataSetRequest.organisationId,
            dataSetId: this.currentDataSetRequest.dataSetId,
            filters: this.createFilters(),
        };

        if (this.dashboardConfig.version == 3) {
            // Set all widgets to loading before the refresh
            this.dashboardConfig.rows.forEach((row) =>
                row.containers.forEach((container) =>
                    container.widgetRows.forEach((widgetRow) => widgetRow.widgets.forEach((w) => (w.loading = true)))
                )
            );

            // Emit to the subject — the switchMap subscription in ngOnInit handles the HTTP call
            // and cancels any prior in-flight request if filters changed rapidly.
            this._v3RefreshSubject.next();
        }
    }

    createFilters(): DashboardFilterRequestDTO[] {
        let result: DashboardFilterRequestDTO[] = [];
        this.dashboardConfig.filters.forEach((filter) => {
            switch (filter.filterTypeInd) {
                case DashboardFilterTypeInd.GeographicLevelSelection:
                    result.push({
                        filterTypeInd: filter.filterTypeInd,
                        filterId: filter.filterId,
                        filterValues: filter.selection,
                    });
                    break;

                default:
                    result.push({
                        filterTypeInd: filter.filterTypeInd,
                        filterId: filter.filterId,
                        filterValues: filter.selection,
                    });
                    break;
            }
        });

        return result;
    }

    /** A filter was updated */
    onFilterChanged() {
        this.updateWidgetDataSetRequest();
    }

    /** A data set was changed */
    onDataSetChanged(value: organisationDataSetChange) {
        this.currentDataSetRequest = value;
        this.updateWidgetDataSetRequest();
    }

    /** A Filter Selection Event has been raised by a Widget */
    onFilterChangeRequestFromWidget(widget: any, e: FilterChangeEvent) {
        let newSelection = [];
        newSelection.push(e.value);

        let updateAllowed = true;

        this.dashboardConfig.filters.forEach((filter) => {
            if (filter.filterTypeInd === e.filterTypeInd) {
                if (e.filterTypeInd == DashboardFilterTypeInd.GeographicLevelSelection) {
                    filter.selectionUpdate = newSelection;
                } else if (!filter.minimumSelection || filter.minimumSelection < 2) {
                    filter.selection = newSelection;
                } else {
                    updateAllowed = false;
                }
            }
        });
        if (updateAllowed) {
            this.updateWidgetDataSetRequest();
        }
    }

    showHelpModal(title: string, content: string, vimeoVideoUrl: string) {
        const helpModal = this.dialog.open(HelpModalComponent, {
            panelClass: 'seDialog',
            width: '800px',
            autoFocus: null,
        });
        helpModal.componentInstance.title = title;
        helpModal.componentInstance.content = content;
        helpModal.componentInstance.vimeoVideoUrl = vimeoVideoUrl;
    }

    mapDialogButtonClicked$ = new Subject<{ url: string; widget: WidgetDefinitionDTO }>();
    subscribeToMapDialogButtonClick(): void {
        let widget: WidgetDefinitionDTO;
        // using a subject/switchMap so that only one dialog is loaded at a time (requests are overwritten by subsequent ones)
        this.mapDialogButtonClicked$
            .pipe(
                tap((value: { url: string; widget: WidgetDefinitionDTO }) => {
                    widget = value.widget;
                    widget.loading = true;
                }),
                takeUntil(this.subscriptionComplete),
                // get the widget map data
                switchMap((value: { url: string; widget: WidgetDefinitionDTO }) =>
                    this.dashboardService.getMapDataForWidget(value.url, this.widgetDataSetRequestDTO).pipe(
                        catchError((error) => {
                            this.handleError(error);
                            widget.loading = false;
                            return of(undefined);
                        }),
                        filter((x) => x)
                    )
                )
            )
            .subscribe(
                (mapData: MapDataDto) => {
                    // open the map dialog
                    this.dialog.open(MapModalComponent, {
                        width: '1600px',
                        height: '800px',
                        maxHeight: 'calc(100vh - 96px)',
                        maxWidth: 'calc(100vw - 96px)',
                        panelClass: 'seDialog',
                        data: { mapData: mapData, title: `${widget.title} - Map` },
                    });
                    widget.loading = false;
                },
                () => (widget.loading = false)
            );
    }

    toggleContainerCollapse(container: WidgetContainerDTO, isCollapsed: boolean): void {
        container.isCollapsed = isCollapsed;
        if (!isCollapsed) {
            this.latestOpenedContainer = container;
        }
    }

    handleError(err: any) {
        this.loading = false;
        if (err.error.status == 403) this.router.navigate(['/login']);
        else {
            this.messageService.showError('Something went wrong.');
        }
    }

    saveCurrentView(): void {
        const newView = {
            id: 0,
            name: '',
            dashboardTypeInd: this.dashboardTypeInd,
            isDefaultView: false,
            sharingLevel: SharingLevel.Private,
            isCreator: true,
            isReadOnly: false,
            userGroups: [],
            filters: [],
            containers: [],
            widgets: [],
        };

        this.openDashboardViewModal(newView, false);
    }

    openDashboardViewModal(view: DashboardViewDto, isEdit: boolean): void {
        const modal = this.dialog.open(DashboardViewModalComponent, {
            panelClass: 'seDialog',
            width: '500px',
            minHeight: '250px',
            data: {
                view: view,
                filters: this.createFilters(),
                containers: this.dashboardConfig.rows
                    .flatMap((row: WidgetContainerRowDTO) => row.containers)
                    .filter((container: WidgetContainerDTO) => container.id),
                widgets: this.dashboardConfig.rows.flatMap((row: WidgetContainerRowDTO) =>
                    row.containers.flatMap((container: WidgetContainerDTO) =>
                        container.widgetRows.flatMap((widgetRow: WidgetRowDto) => widgetRow.widgets)
                    )
                ),
                isEdit: isEdit,
                canEdit: !isEdit || !view.isReadOnly || view.isCreator || this.isUserAdmin,
            },
        });
        modal
            .afterClosed()
            .pipe(
                filter((result: boolean) => result),
                switchMap(() => this.dashboardService.getDashboardViews(this.dashboardTypeInd)),
                takeUntil(this.subscriptionComplete)
            )
            .subscribe((views: DashboardViewDto[]) => {
                this.dashboardConfig.dashboardViews = views;
            });
    }

    loadDashboardView(view: DashboardViewDto): void {
        view.filters.forEach((viewFilter: DashboardViewFilterDto) => {
            if (viewFilter.widgetId) {
                const widget = this.dashboardConfig.rows
                    .flatMap((row) => row.containers)
                    .flatMap((container) => container.widgetRows)
                    .flatMap((widgetRow) => widgetRow.widgets)
                    .find((widget) => widget.id == viewFilter.widgetId);

                if (widget) {
                    const widgetFilter = widget.widgetFilters.find(
                        (widgetFilter) => widgetFilter.filterId == viewFilter.filterId
                    );

                    if (widgetFilter) {
                        widgetFilter.filterSelection = viewFilter.filterSelection[0];
                    }
                }
            } else {
                const dashboardFilter = this.dashboardConfig.filters.find(
                    (filter) => filter.filterId == viewFilter.filterId
                );

                if (dashboardFilter) {
                    dashboardFilter.selection = viewFilter.filterSelection;
                }
            }
        });

        this.dashboardConfig.rows
            .flatMap((x) => x.containers)
            .forEach((container: WidgetContainerDTO) => {
                const viewContainer = view.containers.find(
                    (viewContainer: DashboardViewWidgetContainerDto) => viewContainer.widgetContainerId == container.id
                );
                if (viewContainer) {
                    container.isCollapsed = viewContainer.isCollapsed;
                    container.collapseOnUpdate = false;
                }
            });

        this.dashboardConfig.rows
            .flatMap((row) => row.containers)
            .flatMap((container) => container.widgetRows)
            .flatMap((widgetRow) => widgetRow.widgets)
            .forEach((widget: WidgetDefinitionDTO) => {
                const viewWidget = view.widgets.find(
                    (viewWidget: DashboardViewWidgetDto) => viewWidget.widgetId == widget.id
                );
                widget.sortFieldIndex = viewWidget?.sortFieldIndex;
                widget.sortFieldAscending = viewWidget?.sortFieldAscending;
                widget.legendSelection = viewWidget?.legendSelection;
                widget.tableFilter = viewWidget?.tableFilter;
            });

        this.updateWidgetDataSetRequest();
    }

    editDashboardView(event: MouseEvent, view: DashboardViewDto): void {
        event.stopPropagation();
        event.preventDefault();
        this.openDashboardViewModal(view, true);
    }
}
